import { useState, useEffect, useMemo, useRef } from "react";
import { fetchTeamPitcherUtilization } from "../../../services/team-service";

const clampNumber = (v) => (Number.isFinite(v) ? v : null);

export const usePitcherUtilization = ({ teamId, years }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [selectedYear, setSelectedYear] = useState(years?.[0] ?? null);
  const isInitializingRef = useRef(true);
  const skipNextEffectRef = useRef(false);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const controller = new AbortController();

    (async () => {
      setStatus("loading");
      try {
        const res = await fetchTeamPitcherUtilization(teamId, selectedYear, controller.signal);
        if (controller.signal.aborted) return;

        setData(res);

        if (isInitializingRef.current && !selectedYear && res?.available_years?.length) {
          isInitializingRef.current = false;
          skipNextEffectRef.current = true;
          if (!controller.signal.aborted) setSelectedYear(res.available_years[0]);
        } else {
          isInitializingRef.current = false;
        }

        if (!controller.signal.aborted) setStatus("ready");
      } catch (e) {
        if (e?.name !== "AbortError") {
          setStatus("error");
          isInitializingRef.current = false;
        }
      }
    })();

    return () => controller.abort();
  }, [teamId, selectedYear]);

  const availableYears = useMemo(() => data?.available_years || years || [], [data, years]);

  const { rotation, bullpen, totalGames } = useMemo(() => {
    const pitchers = data?.pitchers || [];
    if (!pitchers.length) return { rotation: [], bullpen: [], totalGames: 1 };
    const tg = pitchers[0]?.total_games ?? 1;
    const threshold = Math.ceil(tg * 0.1);
    const rot = pitchers.filter((p) => (p.starts ?? 0) >= threshold).slice(0, 5);
    const bull = pitchers
      .filter((p) => (p.starts ?? 0) < threshold && (p.games ?? 0) >= 2)
      .sort((a, b) => {
        const aGmli = clampNumber(a.relief_gmli ?? a.gmli) ?? 0;
        const bGmli = clampNumber(b.relief_gmli ?? b.gmli) ?? 0;
        return bGmli - aGmli;
      });
    return { totalGames: tg, rotation: rot, bullpen: bull };
  }, [data]);

  return { status, selectedYear, setSelectedYear, availableYears, rotation, bullpen, totalGames };
};
